import { Injectable } from '@angular/core';
import { forkJoin, iif, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map, mapTo, tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import FileSaver from 'file-saver';
import { environment } from '@env/environment';
import { DialogService } from '@shared/services/dialog/dialog.service';
import { ReportInfo, ReportInfoResponseDTOS } from '@shared/models/report-info';
import { ReportDigest } from '../models/report-digest';
import { AssetInfo } from '../models/pdf-asset-info';

@Injectable({
  providedIn: 'root',
})
export class ReportService{
  private baseUrl = environment.reportService;
  private clUrl = environment.contentLakeBaseUrl;
  private readonly CL_HEADERS = new HttpHeaders()
    .set('X-TI-AGENT-ID', environment.entitlementAgentId)
    .set('only-latest-created', 'true')
    .set('Accept', 'application/json')
    .set('Content-Type', 'application/json');

  constructor(
    private httpClient: HttpClient,
    private dialogService: DialogService,
  ) {}

    /**
     * Gets a list of reportCodes associated with a matCode
     *
     * @param matCode the matCode to look for
     */
    getReportCodesByMatCode(matCode: string): Observable<Array<string>> {
      const queryParams = new HttpParams().set('matCode', matCode);
      const url = `${this.baseUrl}/reports/by/matCode`;
      const headers = new HttpHeaders().set('Accept', 'application/json');
      return this.httpClient.get<any>(url, { headers, params: queryParams }).pipe(
        map( (response)=> {
          return response.reportInfoResponseDTOS.map( (element:any) => { return element.productCode })
        })
      );
    }

  /**
   * Gets report information
   *
   * @param reportCode the reportCode to look for
   */
  getReportByCode(reportCode: string): Observable<ReportInfo> {
    const url = `${this.baseUrl}/report/info/${reportCode}`;
    const headers = new HttpHeaders().set('Accept', 'application/json');

    return this.httpClient.get<ReportInfo>(url, { headers })
  }

  /**
   * for a given matCode it gets list of ReportInfo for this matCode and its children.
   *
   * @param matCode a matcode
   * @returns array of ReportInfo
   */
  getReportsRecursivelyForMatCode(matCode: string): Observable<ReportInfo[]> {
    const queryParams = new HttpParams().set('matCode', matCode)
    const url = `${this.baseUrl}/reports/expanded/matCode`
    const headers = new HttpHeaders().set('Accept', 'application/json');

    return this.httpClient.get<ReportInfoResponseDTOS>(url, { headers, params: queryParams }).pipe(
      map( response => response.reportInfoResponseDTOS)
    );
  }

  /**
   * Gets the url for a given PDF report
   * @param objectId The content lake object id for the report
   * @returns url of the PDF to be shown
   */
  getReportURL(objectId: string): Observable<AssetInfo[]> {
    const requestBody = {
      properties: [
        {
          property: 'DERIVED_FROM',
          value: objectId,
        },
      ],
    };

    return forkJoin([
      this.httpClient
        .post<any>(`${this.clUrl}/objects/search/`, requestBody, { headers: this.CL_HEADERS })
        .pipe(catchError((err) => (err.status === 403 ? throwError(() => err) : of({ objects: [] })))),
      this.httpClient.get(`${this.clUrl}/objects/${objectId}`, { headers: this.CL_HEADERS }),
    ]).pipe(
      map(([clObject, pdfObject]: any) => {
        if (clObject.objects.length === 0) {
          return [
            {
              url: pdfObject.url,
              clObjectId: pdfObject.object_id,
              downloadUrl: pdfObject.url,
              signedUrl: pdfObject.url,
              filename: pdfObject.metadata.original_filename,
              type: pdfObject.assetType,
              mimetype: pdfObject.mimetype,
            },
          ];
        }

        return clObject.objects.map((object) => ({
          url: object.url,
          clObjectId: object.metadata.DERIVED_FROM,
          downloadUrl: pdfObject.url,
          signedUrl: pdfObject.url,
          filename: pdfObject.metadata.original_filename,
          type: object.metadata.DERIVATION_TYPE,
          mimetype: object.mimetype,
        }));
      })
    );
  }

  /**
   * Example of how to call downloadPdf:
   *
   * yourFunctionToDownloadPdf() {
   *    this.downloadingPdf = true; // variable to handle the spinner
   *    this.reportService.getReportURL(this.pdfAssetInfo.clObjectId)
   *      .subscribe(pdfAssetInfo => {
   *        this.reportService.downloadPdf(pdfAssetInfo)
   *        .subscribe(() => this.downloadingPdf = false);
   *      })
   *  }
   */
  downloadPdf(pdfAssetInfo: AssetInfo): Observable<void> {
    return this.httpClient.get(pdfAssetInfo.downloadUrl, { responseType: 'blob' })
      .pipe(
        tap(blob => FileSaver.saveAs(blob, pdfAssetInfo.filename)),
        mapTo(void 0),
        catchError((error) => {
          this.dialogService.openError();
          return throwError(error);
        }),
      )
  }

  getReportByReportCode(reportCode: string): Observable<ReportDigest> {
    const url = `${this.baseUrl}/report/info/${reportCode}`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    return this.httpClient.get<any>(url, { headers }).pipe(
      concatMap((res: any) => iif(() => res.clObjectId,
        this.getArtifactNameAndMimeType(res.clObjectId),
        of({
          artifactName: res.name,
          artifactType: 'application/hygraph'
        })
      ).pipe(
        map(artifact => [res, artifact])
      )),
      map(([result, artifact]) => ({
       hygraphId: result.hygraphId,
       id: result.clObjectId,
       reportName: result.name,
       reportCode: result.productCode,
       artifactName: artifact.artifactName,
       artifactType: artifact.artifactType,
       description: result.description,
       marketAvailability: result.marketAvailability,
       publishDate: result.releaseDate,
       estimatedWork: result.estimatedWork,
       signedUrl: artifact.signedUrl
      })
    ));
  }

  private getArtifactNameAndMimeType(objectId: string) {
    const url = `${this.clUrl}/objects/${objectId}`
    return this.httpClient.get(url, { headers: this.CL_HEADERS }).pipe(
      map((response: any) => ({
        artifactName: response.metadata.original_filename,
        artifactType: response.mimetype,
        signedUrl: response.url
      }))
    )
  }
}


